import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { AsyncTypeahead, TypeaheadInputMulti } from 'react-bootstrap-typeahead';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import useUpdateEffect from '../../../Hooks/useUpdateEffect';

// COMPONENTS
import Field from './Field';
import Token from './Token';

// STYLES
import style from '../../../../scss/components/form.module.scss';
import '../../../../scss/external/select.scss';
import Button from '../Buttons/Button';
import classNames from "classnames";

const OrderableAutoComplete = ({
    onChange,
    entity,
    placeholder,
    searchBy,
    isMultiple = false,
    isReversable = false,
    isDeleteAll = false,
    emptyLabel,
    newSelectionPrefix,
    ...props
}) => {
    const { name, service, reference, allowNew } = props;
    const { t } = useTranslation();

    const [state, setState] = useState({
        items: [],
        selected: entity[name]
            ? (Array.isArray(entity[name]) && entity[name]) || [entity[name]]
            : [],
        isLoading: false,
    });

    const update = (change) =>
        setState((oldState) => ({ ...oldState, ...change }));

    const onSearch = (query) => {
        update({ isLoading: true });

        service
            .autocomplete(reference, {
                [searchBy]: query,
                withoutId: entity.id,
            })
            .then((result) => {
                const items = [...result.data];

                update({ items, isLoading: false });
            });
    };

    const onMove = useCallback(
        (dragIndex, hoverIndex) => {
            const item = state.selected[dragIndex];
            const newSelected = state.selected.slice();
            newSelected.splice(dragIndex, 1);
            newSelected.splice(hoverIndex, 0, item);

            setState((state) => ({ ...state, selected: newSelected }));
        },
        [state.selected],
    );

    const onSelectionChange = (selection) => {
        update({ selected: selection });
    };

    const handleReverse = () => {
        const newSelected = state.selected.toReversed();
        setState((state) => ({ ...state, selected: newSelected }));
    };

    const handleDeleteAll = () => {
        setState((state) => ({ ...state, selected: [] }));
    };

    useUpdateEffect(() => {
        if (isMultiple) {
            onChange({ [name]: state.selected });
        } else {
            onChange({ [name]: state.selected[0] || null });
        }
    }, [state.selected]);

    return (
        <div className={classNames(style.formGroup, props.className)}>
            <Field {...props}>
                {(hasError, attributes, isChanged) => {
                    return (
                        <DndProvider backend={HTML5Backend}>
                            <AsyncTypeahead
                                id={`${entity.name}.${name}-select`}
                                className={`
                                ${style.autocomplete}
                                -${name}
                                ${isChanged ? style.changedInput : ''}
                            `}
                                isLoading={state.isLoading}
                                onSearch={onSearch}
                                promptText={placeholder}
                                minLength={3}
                                multiple={isMultiple}
                                labelKey={searchBy}
                                options={state.items}
                                selected={state.selected}
                                flip
                                emptyLabel={
                                    emptyLabel ?? t('application.noResults')
                                }
                                newSelectionPrefix={
                                    newSelectionPrefix ??
                                    t('application.createNew')
                                }
                                searchText={t('application.search')}
                                onChange={onSelectionChange}
                                inputProps={{
                                    id: attributes.id,
                                    className: `
                                    ${style.textArea}
                                    ${hasError ? style.isInvalid : ''}
                                `,
                                }}
                                allowNew={allowNew}
                                {...attributes}
                                renderInput={(inputProps, typeaheadProps) => (
                                    <TypeaheadInputMulti
                                        {...inputProps}
                                        selected={state.selected}
                                    >
                                        {state.selected.map((option, ind) => (
                                            <Token
                                                index={ind}
                                                key={
                                                    option?.id ??
                                                    option?.uuid ??
                                                    option?.label ??
                                                    ind
                                                }
                                                onMove={onMove}
                                                onRemove={
                                                    typeaheadProps.onRemove
                                                }
                                                option={option}
                                            >
                                                {option?.name ??
                                                    option?.label ??
                                                    option?.title ??
                                                    '?'}
                                            </Token>
                                        ))}
                                    </TypeaheadInputMulti>
                                )}
                            />
                        </DndProvider>
                    );
                }}
            </Field>

            {isDeleteAll && (
                <div>
                    <Button
                        name="deleteAll"
                        click={handleDeleteAll}
                        classNames="w-full p-4 bg-light-yoghurt rounded-lg mt-2"
                    />
                </div>
            )}

            {isReversable && (
                <Button
                    name="order_reverse"
                    click={handleReverse}
                    classNames="w-full p-4 bg-light-yoghurt rounded-lg mt-2"
                />
            )}
        </div>
    );
};

export default OrderableAutoComplete;

OrderableAutoComplete.propTypes = {
    /**
     * Type of entity
     */
    entity: PropTypes.object.isRequired,
    /**
     * Type of onChange
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Type of placeholder
     */
    placeholder: PropTypes.string.isRequired,
    /**
     * Type of searchBy
     */
    searchBy: PropTypes.string.isRequired,
    /**
     * Type of service
     */
    service: PropTypes.object.isRequired,
    /**
     * Type of reference
     */
    reference: PropTypes.string.isRequired,
    /**
     * Type of isMultiple
     */
    isMultiple: PropTypes.bool.isRequired,
    /**
     * Type of name
     */
    name: PropTypes.string.isRequired,
    allowNew: PropTypes.bool,
    isReversable: PropTypes.bool,
    emptyLabel: PropTypes.string,
    newSelectionPrefix: PropTypes.string,
    className: PropTypes.string,
};

OrderableAutoComplete.defaultProps = {
    allowNew: false,
    isReversable: false,
    emptyLabel: null,
    newSelectionPrefix: null,
    className: undefined,
};
